import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearAuthTokens, getUserEmail } from '../../services/auth';
import { useMe } from '../../store/useMe';
import { NavIcon } from './NavIcons';
import '../../styles/admin.css';

const navClass = ({ isActive }: { isActive: boolean }) => `admin-nav-link${isActive ? ' active' : ''}`;

// Maps the current admin route to its section title (i18n key).
const SECTION_KEYS: Record<string, string> = {
  '/admin': 'admin:navDashboard',
  '/admin/orders': 'admin:navOrders',
  '/admin/leads': 'admin:navLeads',
  '/admin/inventory': 'admin:navInventory',
  '/admin/users': 'admin:navUsers',
  '/admin/documents': 'admin:navDocuments',
  '/admin/settings': 'admin:navSettings',
  '/admin/profile': 'admin:navProfile',
};

export const AdminLayout: React.FC = () => {
  const { t, i18n } = useTranslation(['admin', 'auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { me, fetchMe } = useMe();
  const isAdmin = me?.isAdmin ?? false;
  // Mobile drawer: the sidebar slides in over the content on narrow screens.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Any navigation closes the drawer.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Admin panel is Ukrainian-only (no language switcher).
  useEffect(() => {
    if (i18n.language !== 'uk') i18n.changeLanguage('uk');
  }, [i18n]);

  const sectionKey = SECTION_KEYS[location.pathname] ?? 'admin:navDashboard';

  const handleLogout = () => {
    clearAuthTokens();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="admin-shell">
      {menuOpen && <div className="admin-sidebar-overlay" onClick={() => setMenuOpen(false)} />}
      <aside className={`admin-sidebar${menuOpen ? ' is-open' : ''}`}>
        <Link to="/admin" className="admin-brand">
          <img src="/logo.png" className="admin-brand-logo" alt="EM GROUP" />
          <span className="admin-brand-text">{t('common:admin')}</span>
        </Link>

        <nav className="admin-nav">
          <NavLink to="/admin" end className={navClass}>
            <span className="admin-nav-icon"><NavIcon name="dashboard" /></span>
            {t('admin:navDashboard')}
          </NavLink>

          <div className="admin-nav-group">{t('admin:groupOperations')}</div>
          <NavLink to="/admin/orders" className={navClass}>
            <span className="admin-nav-icon"><NavIcon name="orders" /></span>
            {t('admin:navOrders')}
          </NavLink>
          <NavLink to="/admin/leads" className={navClass}>
            <span className="admin-nav-icon"><NavIcon name="leads" /></span>
            {t('admin:navLeads')}
          </NavLink>
          <NavLink to="/admin/inventory" className={navClass}>
            <span className="admin-nav-icon"><NavIcon name="inventory" /></span>
            {t('admin:navInventory')}
          </NavLink>

          {isAdmin && (
            <>
              <div className="admin-nav-group">{t('admin:groupDocuments')}</div>
              <NavLink to="/admin/documents" className={navClass}>
                <span className="admin-nav-icon"><NavIcon name="documents" /></span>
                {t('admin:navDocuments')}
              </NavLink>

              <div className="admin-nav-group">{t('admin:groupAdmin')}</div>
              <NavLink to="/admin/users" className={navClass}>
                <span className="admin-nav-icon"><NavIcon name="users" /></span>
                {t('admin:navUsers')}
              </NavLink>
              <NavLink to="/admin/settings" className={navClass}>
                <span className="admin-nav-icon"><NavIcon name="settings" /></span>
                {t('admin:navSettings')}
              </NavLink>
            </>
          )}
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/admin/profile" className="admin-sidebar-link"><NavIcon name="profile" size={15} /> {t('admin:navProfile')}</Link>
          <div className="admin-sidebar-user">
            <Link to="/admin/profile" className="admin-sidebar-email" title={getUserEmail() ?? ''}>
              {getUserEmail()}
            </Link>
            <button type="button" className="admin-logout" onClick={handleLogout}>
              {t('auth:logout')}
            </button>
          </div>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-menu-btn"
            aria-label="Меню"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>
          <nav className="admin-breadcrumb" aria-label="breadcrumb">
            <span>{t('common:admin')}</span>
            <span className="admin-breadcrumb-sep">/</span>
            <span className="admin-breadcrumb-current">{t(sectionKey)}</span>
          </nav>
        </header>
        <div className="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
